import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const TABS = ['Show History', 'Artists Hosted', 'Stages'];
const PAGE_SIZE = 25;

function Pagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);

    return (
        <div style={styles.pagination}>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
            >
                ‹ Prev
            </button>
            {pages.map(p => (
                <button
                    key={p}
                    onClick={() => onPageChange(p)}
                    style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}
                >
                    {p}
                </button>
            ))}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}
            >
                Next ›
            </button>
        </div>
    );
}

function ShowHistoryTab({ shows }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded at this venue yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? shows.filter(show => {
        const dateStr = show.event_date
            ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
            : '';
        return [dateStr, show.artists, show.stage_name, show.festival_name]
            .some(val => val && val.toLowerCase().includes(q));
    }) : shows;

    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, artists, or stage..."
                value={search}
                onChange={handleSearch}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Show #</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Artists</th>
                                    <th style={styles.th}>Stage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj
                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';

                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={styles.td}>{formattedDate}</td>
                                            <td style={{ ...styles.td, color: '#ccc', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.artists ? ` - ${show.artists}` : ''}</>
                                                    : show.artists || '—'
                                                }
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.stage_name || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function ArtistsHostedTab({ artists }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!artists || artists.length === 0) return <div style={styles.emptyTab}>No artist data recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? artists.filter(a =>
        [a.name, a.city, a.state, a.country]
            .some(val => val && val.toLowerCase().includes(q))
    ) : artists;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by artist, city, state, country..."
                value={search}
                onChange={handleSearch}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No artists match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Artist</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Appearances</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((artist, idx) => (
                                    <tr key={artist.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}>
                                            <Link to={`/artists/${artist.slug || artist.id}`} style={styles.tableLink}>{artist.name}</Link>
                                        </td>
                                        <td style={styles.td}>{artist.city || '—'}</td>
                                        <td style={styles.td}>{artist.state || '—'}</td>
                                        <td style={styles.td}>{artist.country || '—'}</td>
                                        <td style={styles.td}>
                                            <span style={styles.showCount}>{artist.appearances}x</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function StagesTab({ stages, venueId, onStagesUpdated }) {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const emptyForm = { name: '', capacity: '', description: '', is_default: false };
    const [form, setForm] = useState(emptyForm);

    if (!stages) return <div style={styles.emptyTab}>Loading stages...</div>;

    const handleAdd = () => {
        setForm(emptyForm);
        setEditingId(null);
        setShowAddForm(true);
        setDeleteError('');
    };

    const handleEdit = (stage) => {
        setForm({
            name: stage.name || '',
            capacity: stage.capacity != null ? String(stage.capacity) : '',
            description: stage.description || '',
            is_default: stage.is_default || false,
        });
        setEditingId(stage.id);
        setShowAddForm(false);
        setDeleteError('');
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setEditingId(null);
        setForm(emptyForm);
        setDeleteError('');
    };

    const handleSaveNew = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            await axios.post(`${config.API_URL}/venues/${venueId}/stages`, {
                name: form.name.trim(),
                capacity: form.capacity ? parseInt(form.capacity) : null,
                description: form.description.trim() || null,
                is_default: form.is_default,
            });
            setShowAddForm(false);
            setForm(emptyForm);
            onStagesUpdated();
        } catch (err) {
            console.error('Failed to create stage', err);
        }
        setSaving(false);
    };

    const handleSaveEdit = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            await axios.put(`${config.API_URL}/stages/${editingId}`, {
                name: form.name.trim(),
                capacity: form.capacity ? parseInt(form.capacity) : null,
                description: form.description.trim() || null,
                is_default: form.is_default,
            });
            setEditingId(null);
            setForm(emptyForm);
            onStagesUpdated();
        } catch (err) {
            console.error('Failed to update stage', err);
        }
        setSaving(false);
    };

    const handleDelete = async (stage) => {
        if (stage.is_default) {
            setDeleteError(`"${stage.name}" is the default stage and cannot be deleted. Set another stage as default first.`);
            return;
        }
        if (!window.confirm(`Delete stage "${stage.name}"? Any events linked to this stage will be unlinked.`)) return;
        setDeleteError('');
        try {
            await axios.delete(`${config.API_URL}/stages/${stage.id}`);
            onStagesUpdated();
        } catch (err) {
            setDeleteError('Failed to delete stage.');
        }
    };

    return (
        <div>
            {deleteError && (
                <div style={styles.stageError}>{deleteError}</div>
            )}

            {stages.length === 0 ? (
                <div style={styles.emptyTab}>No stages recorded yet.</div>
            ) : (
                <div style={styles.stageList}>
                    {stages.map(stage => (
                        <div key={stage.id} style={styles.stageCard}>
                            {editingId === stage.id ? (
                                <StageForm
                                    form={form}
                                    setForm={setForm}
                                    onSave={handleSaveEdit}
                                    onCancel={handleCancel}
                                    saving={saving}
                                />
                            ) : (
                                <div style={styles.stageCardInner}>
                                    <div style={styles.stageCardLeft}>
                                        <div style={styles.stageName}>
                                            {stage.name}
                                            {stage.is_default && (
                                                <span style={styles.defaultBadge}>Default</span>
                                            )}
                                        </div>
                                        {stage.capacity && (
                                            <div style={styles.stageMeta}>Capacity: {stage.capacity.toLocaleString()}</div>
                                        )}
                                        {stage.description && (
                                            <div style={styles.stageDesc}>{stage.description}</div>
                                        )}
                                    </div>
                                    <div style={styles.stageCardActions}>
                                        <button onClick={() => handleEdit(stage)} style={styles.stageEditBtn}>Edit</button>
                                        <button onClick={() => handleDelete(stage)} style={styles.stageDeleteBtn}>Delete</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showAddForm && (
                <div style={{ ...styles.stageCard, marginTop: '16px' }}>
                    <div style={styles.stageFormTitle}>New Stage</div>
                    <StageForm
                        form={form}
                        setForm={setForm}
                        onSave={handleSaveNew}
                        onCancel={handleCancel}
                        saving={saving}
                    />
                </div>
            )}

            {!showAddForm && (
                <button onClick={handleAdd} style={styles.addStageBtn}>+ Add Stage</button>
            )}
        </div>
    );
}

function StageForm({ form, setForm, onSave, onCancel, saving }) {
    return (
        <div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Stage Name *</label>
                <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Main Stage, Tent Stage"
                    style={styles.stageInput}
                />
            </div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Capacity</label>
                <input
                    type="number"
                    value={form.capacity}
                    onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                    placeholder="Stage capacity"
                    style={{ ...styles.stageInput, width: '180px' }}
                />
            </div>
            <div style={styles.stageFormRow}>
                <label style={styles.stageFormLabel}>Description</label>
                <textarea
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Optional notes about this stage"
                    rows={2}
                    style={{ ...styles.stageInput, resize: 'vertical' }}
                />
            </div>
            <div style={{ ...styles.stageFormRow, alignItems: 'center', gap: '10px', flexDirection: 'row' }}>
                <input
                    type="checkbox"
                    id="is_default_stage"
                    checked={form.is_default}
                    onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
                    style={{ accentColor: '#bb86fc', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label htmlFor="is_default_stage" style={{ ...styles.stageFormLabel, margin: 0, cursor: 'pointer' }}>
                    Set as default stage
                </label>
            </div>
            <div style={styles.stageFormActions}>
                <button onClick={onSave} disabled={saving || !form.name.trim()} style={styles.stageSaveBtn}>
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={onCancel} style={styles.stageCancelBtn}>Cancel</button>
            </div>
        </div>
    );
}

function VenueDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    const fetchVenue = () => {
        axios.get(`${config.API_URL}/venues/${slug}`)
            .then(res => {
                setData(res.data);
                setLoading(false);
                if (res.data && res.data.venue) {
                    document.title = `SHARC - ${res.data.venue.name}`;
                }
            })
            .catch(() => {
                setError('Failed to load venue');
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchVenue();
        return () => { document.title = 'SHARC - Shows Archive'; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [slug]);

    if (loading) return <div style={styles.message}>Loading venue...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { venue, stats, links, stages, show_history, artists_hosted } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
        });
    };

    return (
        <div>
            {/* Nav row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Link to="/venues" style={styles.backLink}>← Back to Venues</Link>
                <Link to={`/venues/${venue.slug || venue.id}/edit`} style={styles.editButton}>Edit Venue</Link>
            </div>

            {/* Venue Header */}
            <div style={styles.header}>
                <div style={venue.image_url ? styles.headerWithImage : undefined}>
                    {venue.image_url && (
                        <img
                            src={venue.image_url}
                            alt={venue.name}
                            style={styles.venueImage}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <div style={styles.headerText}>
                        <h1 style={styles.title}>{venue.name}</h1>

                        <div style={styles.location}>
                            {[venue.street, venue.city, venue.state, venue.country].filter(Boolean).join(', ')}
                            {venue.postal_code && ` ${venue.postal_code}`}
                        </div>

                        <div style={styles.tags}>
                            {venue.venue_type && <span style={styles.tag}>{venue.venue_type}</span>}
                            {venue.age_limit && <span style={styles.tag}>{venue.age_limit}</span>}
                            <span style={{
                                ...styles.tag,
                                color: venue.status === 'Open' ? '#03dac6' : venue.status === 'Closed' ? '#cf6679' : '#ffb74d'
                            }}>
                                {venue.status}
                            </span>
                        </div>
                    </div>
                </div>

                {venue.description && (
                    <div style={styles.bio}>{venue.description}</div>
                )}
            </div>

            {/* Links */}
            {links && links.length > 0 && (
                <div style={styles.linksRow}>
                    {links.map(link => (
                        <a
                            key={link.id}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.linkChip}
                        >
                            {link.description} ↗
                        </a>
                    ))}
                </div>
            )}

            {/* Key Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{venue.capacity ? venue.capacity.toLocaleString() : '—'}</div>
                    <div style={styles.statLabel}>Capacity</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total_shows || 0}</div>
                    <div style={styles.statLabel}>Total Shows</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{artists_hosted ? artists_hosted.length : 0}</div>
                    <div style={styles.statLabel}>Artists Hosted</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>
                        {stats.first_show_date ? new Date(stats.first_show_date).getFullYear() : '—'}
                    </div>
                    <div style={styles.statLabel}>First Show</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>
                        {stats.last_show_date ? new Date(stats.last_show_date).getFullYear() : '—'}
                    </div>
                    <div style={styles.statLabel}>Last Show</div>
                </div>
            </div>

            {/* First / Last show date bar */}
            <div style={styles.historyGrid}>
                <div style={styles.historyItem}>
                    <div style={styles.historyLabel}>First Show</div>
                    <div style={styles.historyValue}>{formatDate(stats.first_show_date)}</div>
                </div>
                <div style={styles.historyItem}>
                    <div style={styles.historyLabel}>Most Recent Show</div>
                    <div style={styles.historyValue}>{formatDate(stats.last_show_date)}</div>
                </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    {TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(i)}
                            style={{
                                ...styles.tabBtn,
                                ...(activeTab === i ? styles.tabBtnActive : {}),
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div style={styles.tabContent}>
                    {activeTab === 0 && <ShowHistoryTab shows={show_history} />}
                    {activeTab === 1 && <ArtistsHostedTab artists={artists_hosted} />}
                    {activeTab === 2 && (
                        <StagesTab
                            stages={stages}
                            venueId={venue.id}
                            onStagesUpdated={fetchVenue}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold' },
    header: { marginBottom: '24px' },
    headerWithImage: { display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '16px' },
    headerText: { flex: 1 },
    venueImage: { width: '140px', height: '140px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    title: { fontSize: '40px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '6px' },
    location: { fontSize: '16px', color: '#aaa', marginBottom: '8px' },
    tags: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' },
    tag: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc' },
    bio: { fontSize: '15px', color: '#ccc', lineHeight: '1.7', marginTop: '16px', padding: '16px', backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #2a2a2a' },
    linksRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px' },
    linkChip: { backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '4px', padding: '6px 14px', fontSize: '13px', color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '36px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    historyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' },
    historyItem: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px' },
    historyLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
    historyValue: { fontSize: '18px', fontWeight: 'bold', color: '#e0e0e0' },
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', borderBottom: '2px solid #333', marginBottom: '0', overflowX: 'auto', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '200px' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    pagination: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px', flexWrap: 'wrap' },
    pageBtn: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', color: '#aaa', cursor: 'pointer' },
    pageBtnActive: { backgroundColor: '#bb86fc', borderColor: '#bb86fc', color: '#121212', fontWeight: 'bold' },
    pageBtnDisabled: { opacity: 0.35, cursor: 'default' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '60px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
    // Stages tab styles
    stageList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    stageCard: { backgroundColor: '#222', border: '1px solid #333', borderRadius: '8px', padding: '16px' },
    stageCardInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' },
    stageCardLeft: { flex: 1 },
    stageName: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' },
    defaultBadge: { backgroundColor: '#2c2c2c', border: '1px solid #bb86fc', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', letterSpacing: '0.5px' },
    stageMeta: { fontSize: '13px', color: '#aaa', marginBottom: '4px' },
    stageDesc: { fontSize: '13px', color: '#888', marginTop: '6px' },
    stageCardActions: { display: 'flex', gap: '8px', flexShrink: 0 },
    stageEditBtn: { backgroundColor: '#2c2c2c', border: '1px solid #555', borderRadius: '4px', padding: '5px 12px', fontSize: '13px', color: '#ccc', cursor: 'pointer' },
    stageDeleteBtn: { backgroundColor: 'transparent', border: '1px solid #555', borderRadius: '4px', padding: '5px 12px', fontSize: '13px', color: '#cf6679', cursor: 'pointer' },
    stageFormTitle: { fontSize: '14px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    stageFormRow: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' },
    stageFormLabel: { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    stageInput: { backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    stageFormActions: { display: 'flex', gap: '10px', marginTop: '6px' },
    stageSaveBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '8px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    stageCancelBtn: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '8px 16px', fontSize: '14px', cursor: 'pointer' },
    addStageBtn: { marginTop: '16px', backgroundColor: '#2c2c2c', border: '1px solid #555', borderRadius: '6px', padding: '9px 18px', fontSize: '14px', color: '#bb86fc', cursor: 'pointer', fontWeight: 'bold' },
    stageError: { backgroundColor: '#2a1a1a', border: '1px solid #cf6679', borderRadius: '6px', padding: '10px 14px', color: '#cf6679', fontSize: '13px', marginBottom: '16px' },
};

export default VenueDetail;